import { h, Component } from 'preact';
import { trackingProvider, DIMENSION } from '@cube/track';
import { Icon } from '@cube/blocks';
import { withConfig } from '../../../../config';
import cx from 'classnames';
import LinkEvent, { processLinkEvent, linkEventLabel, linkEventIcon, hasLinkText } from '../../../linkevent';
import style from './style.less';
import { ContainerFluid, Row, Column } from '@cube/layout';
import { MobileFooter }  from '../../../footer';
import Logo from '@cube/logo-widget';


/**
 * UserMenu is the fly out menu contents containing apps, services, legal links and settings
 */
@trackingProvider({
	[DIMENSION.componentpath]: 'UserMenu'
})
export class UserMenu extends Component {

	state = {
		settingsOpen: false
	}

	openSettings = () => {
		this.setState({ settingsOpen: true });
	}

	closeSettings = () => {
		this.setState({ settingsOpen: false });
	}

	closeUserNav = () => {
		this.closeSettings();
		this.props.onClose();
	}

	handleEventCallWith = (link, e) => {
		this.handleEvent(e, link);
	}

	handleEvent = (e, link) => {
		let customAction = processLinkEvent(link, e);
		if (customAction) this[customAction]();
	}

	render({ theme, open, onClose, useAnimation, animationDirection, clientLinks, closeButton, leadingAltLinks = [], leadingLinks=[] }, { settingsOpen }) {
		const menuOpen = open ? style.open : style.close;

		return (
			<div class={style[this.context.theme]}>
				<div class={style.ariaWrapper} aria-hidden={(!open).toString()}>
					<ContainerFluid expandColumn class={cx( style['user-menu'], style.header, menuOpen, useAnimation && style[animationDirection])}>
						<div class={style.navContainer}>
							<UserMenuHeader
								theme={theme}
								handleEvent={this.handleEvent}
								onClose={onClose}
								closeButton={closeButton}
							/>
							{ (leadingLinks.length > 0 || leadingAltLinks.length > 0) &&
								<UserMenuNav
									links={[...leadingLinks, ...leadingAltLinks]}
									handleEvent={this.handleEvent}
									class={style['menu-links']}
								/>
							}
							{(clientLinks && clientLinks.length) && clientLinks.map((link) => (
								<UserMenuNav
									links={link.subnav}
									handleEvent={this.handleEvent}
									title={link}
									class={style['menu-links']}
								/>
							))}
						</div>
						<MobileFooter />
					</ContainerFluid>
				</div>
			</div>
		);
	}
}

/**
 * UserMenuHeader is the header of the user menu
 *
 * @param {Object}   props
 * @param {Function} props.onClose on close call back
 * @param {Function} props.handleEvent a callback dispatch clicks to
 */
export function UserMenuHeader({ theme, onClose, handleEvent, closeButton, ...props }) {
	return (
		<header class={cx(props.class, style['menu-header'])}>
			<Row class={style.headerRow}>
				<Column>
					<Logo
						theme={theme}
						size="sm"
						link={false}
					/>
				</Column>
				<Column class={cx('col-auto', style.close)}>
					<LinkEvent link={closeButton} handleEvent={onClose}>
						{linkEventIcon(closeButton, { class: style.icon })}
					</LinkEvent>
				</Column>
			</Row>
		</header>
	);
}

/**
 * UserMenuNav is the set of links for navigation
 *
 * @param {Object}   props
 * @param {Array}    props.links a set of links to display in the navigation
 * @param {String}   props.title a title for the links
 * @param {Function} props.handleEvent a callback dispatch clicks to
 */
export function UserMenuNav({ links, title, handleEvent, theme, ...props }){

	// If one level (no links) and has a url, treat it as a list of one instead of a title
	if (title && title.url && (!links || links.length === 0)) {
		links = [title];
		title = null;
	}

	return (
		<section class={props.class}>
			{ title && hasLinkText(title) &&
				<Row class={style.menuNavRow}>
					<Column>
						<header>
							{title.image && <img src={title.image[theme] || title.image.default || title.image} /> }
							{!title.image && (<span class={style.title}>{linkEventLabel(title)}</span>)}
						</header>
					</Column>
				</Row>
			}
			{ (links && links.length) && links.map((link, i) => {
				const linkComp = (
					<LinkEvent link={link} positionIndex={i} class={style.anchorRow} handleEvent={handleEvent} >
						<Row class={style.menuNavRow}>
							{link.iconName && (
								<Column class="col-auto">
									<div class={style['icon-container']}>
										{!link.image && <Icon name={link.iconName} class={style.icon} />}
									</div>
								</Column>
							)}
							<Column>
								{ link.image && <img src={link.image[theme] || link.image.default || link.image} /> }
								{!link.image && (<span class={style.text}>{linkEventLabel(link)}</span>)}
							</Column>
						</Row>
					</LinkEvent>
				);

				if (link.subnav) {
					return (
						<div>
							{linkComp}
							<UserMenuNav
								links={link.subnav}
								class={cx(style['menu-links'], style.submenu)}
							/>
						</div>
					);
				}
				return linkComp;
			})
			}
		</section>
	);
}

/**
 * UserMenuFooterNav is the footer navigation for the user menu, containing various links
 * @param {Object}   props
 * @param {Array}    props.links a list of link objects
 * @param {Function} props.handleEvent A callback event for when a user clicks the component, used to map actions depending on the item
 */
export function UserMenuFooterNav({ links=[], handleEvent, ...props }){
	return (
		<Column class={props.class}>
			<div class={style.footerLinks}>
				{links.map(link => (
					<LinkEvent link={link} handleEvent={handleEvent}>
						{linkEventLabel(link)}
					</LinkEvent>
				))}
			</div>
		</Column>
	);
}

export default withConfig([

	'headerWidget.mobile.clientLinks',
	'headerWidget.clientLinks',

	'headerWidget.mobile.leadingAltLinks',
	'headerWidget.leadingAltLinks',

	'headerWidget.mobile.leadingLinks',
	'headerWidget.leadingLinks',

	'headerWidget.mobile.legalLinks',
	'headerWidget.legalLinks',

	'headerWidget.mobile.userMenu.menuIcon',

	'headerWidget.mobile.userMenu.closeButton',

	'headerWidget.mobile.userMenu.useAnimation',
	'headerWidget.mobile.userMenu.animationDirection'
])(UserMenu);
